// Mercapoli - Restaurant Menu Management
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Loader2, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { productos as productosApi } from '../../lib/api';
import { toast } from 'sonner';

const RestaurantMenu = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [productos, setProductos] = useState([]);

  useEffect(() => {
    loadProductos();
  }, []);

  const loadProductos = async () => {
    try {
      const data = await productosApi.getAll();
      setProductos(data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const toggleDisponibilidad = async (producto) => {
    try {
      await productosApi.update(producto.producto_id, { 
        disponible: !producto.disponible 
      });
      toast.success(`${producto.nombre} ${!producto.disponible ? 'disponible' : 'no disponible'}`);
      loadProductos();
    } catch (error) {
      toast.error('Error al actualizar producto');
    }
  };

  // Agrupar por categoría
  const categorias = [...new Set(productos.map(p => p.categoria))];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/restaurant')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Gestión de Menú</h1>
            </div>
            <Button className="bg-spicy-coral hover:bg-spicy-coral/90">
              <Plus className="h-4 w-4 mr-2" />
              Agregar
            </Button>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
          </div>
        ) : productos.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No tienes productos en tu menú</p>
            <Button className="bg-spicy-coral hover:bg-spicy-coral/90">
              <Plus className="h-4 w-4 mr-2" />
              Agregar primer producto
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {categorias.map((categoria) => (
              <div key={categoria}>
                <h2 className="font-heading font-bold mb-3">{categoria}</h2>
                <div className="space-y-3">
                  {productos
                    .filter(p => p.categoria === categoria)
                    .map((producto) => (
                      <Card key={producto.producto_id}>
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            {producto.imagen && (
                              <img 
                                src={producto.imagen} 
                                alt={producto.nombre}
                                className="w-20 h-20 rounded-lg object-cover"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-medium">{producto.nombre}</h3>
                                  <p className="text-sm text-muted-foreground line-clamp-1">
                                    {producto.descripcion}
                                  </p>
                                  <p className="font-heading font-bold text-spicy-coral mt-1">
                                    ${producto.precio.toFixed(2)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={producto.disponible}
                                    onCheckedChange={() => toggleDisponibilidad(producto)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantMenu;
